import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/bh`;

const BhLobby = ({ user, onGameStart }) => {
  const [queueSize, setQueueSize] = useState(0);
  const [matchedGameId, setMatchedGameId] = useState(null);
  const [dots, setDots] = useState('');
  const navigate = useNavigate();
  const pollingRef = useRef(null);
  const hasNavigated = useRef(false);

  useEffect(() => {
    const iv = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (matchedGameId && !hasNavigated.current) {
      hasNavigated.current = true;
      toast.success('Partita trovata!');
      onGameStart(matchedGameId);
      navigate(`/bh/game/${matchedGameId}`);
    }
  }, [matchedGameId, navigate, onGameStart]);

  const checkMatchmakingStatus = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/game/join-matchmaking`, { user_id: user.user_id });
      if (response.data.matched) {
        setMatchedGameId(response.data.game_id);
      } else {
        setQueueSize(response.data.queue_size);
      }
    } catch (error) {
      console.error('Errore matchmaking', error);
    }
  }, [user.user_id]);

  const subscribeToQueue = useCallback(() => {
    const queueChannel = supabase.channel('bh_matchmaking_queue_updates')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bh_matchmaking_queue' }, () => {
        checkMatchmakingStatus();
      }).subscribe();
    return () => { queueChannel.unsubscribe(); supabase.removeChannel(queueChannel); };
  }, [user.user_id, checkMatchmakingStatus]);

  const subscribeToGameCreation = useCallback(() => {
    const gameChannel = supabase.channel('bh_game_sessions_creation')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'bh_game_sessions' }, (payload) => {
        const players = payload.new?.players || [];
        if (players.some(p => p.user_id === user.user_id)) setMatchedGameId(payload.new.id);
      }).subscribe();
    return () => { gameChannel.unsubscribe(); supabase.removeChannel(gameChannel); };
  }, [user.user_id]);

  useEffect(() => {
    checkMatchmakingStatus();
    pollingRef.current = setInterval(checkMatchmakingStatus, 2000);
    const unsubQ = subscribeToQueue();
    const unsubG = subscribeToGameCreation();
    return () => {
      clearInterval(pollingRef.current);
      unsubQ(); unsubG();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'linear-gradient(135deg,#1a0510 0%,#2d0820 45%,#1a0510 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '20px 16px', position: 'relative', overflow: 'hidden',
    }}>
      <motion.div animate={{ scale:[1,1.2,1], opacity:[0.3,0.6,0.3] }} transition={{ duration:6, repeat:Infinity }}
        style={{ position:'absolute', top:'15%', left:'15%', width:350, height:350, borderRadius:'50%',
          background:'radial-gradient(circle,rgba(236,72,153,0.3),transparent 70%)', filter:'blur(40px)', pointerEvents:'none' }} />
      <motion.div animate={{ scale:[1.1,1,1.1], opacity:[0.2,0.5,0.2] }} transition={{ duration:8, repeat:Infinity, delay:2 }}
        style={{ position:'absolute', bottom:'15%', right:'15%', width:300, height:300, borderRadius:'50%',
          background:'radial-gradient(circle,rgba(239,68,68,0.25),transparent 70%)', filter:'blur(40px)', pointerEvents:'none' }} />

      <button onClick={() => navigate('/games')} style={{
        position: 'fixed', top: 14, left: 16, zIndex: 50,
        background: 'rgba(15,5,10,0.85)', backdropFilter: 'blur(8px)',
        border: '1px solid rgba(236,72,153,0.3)', borderRadius: 10,
        padding: '6px 14px', color: '#f9a8d4', fontSize: 13, fontWeight: 700, cursor: 'pointer',
      }}>← Survival Games</button>

      <motion.div initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.7 }}
        style={{ position:'relative', zIndex:1, width:'100%', maxWidth:440 }}>

        <div style={{ textAlign:'center', marginBottom:8 }}>
          <motion.div animate={{ opacity:[0.4,1,0.4] }} transition={{ duration:2, repeat:Infinity }}
            style={{ fontSize:32, letterSpacing:10 }}>💔 ❤️ 💔</motion.div>
        </div>

        <div style={{
          background: 'linear-gradient(160deg,rgba(30,5,15,0.98),rgba(20,3,10,0.99))',
          borderRadius: 24, border: '1px solid rgba(236,72,153,0.25)',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.8),0 24px 60px rgba(0,0,0,0.8)',
          padding: '36px 32px', position: 'relative', overflow: 'hidden',
        }}>
          {[{top:12,left:12},{top:12,right:12},{bottom:12,left:12},{bottom:12,right:12}].map((pos,i) => (
            <div key={i} style={{ position:'absolute', ...pos, width:28, height:28,
              borderTop: pos.top!==undefined ? '2px solid rgba(236,72,153,0.3)' : undefined,
              borderBottom: pos.bottom!==undefined ? '2px solid rgba(236,72,153,0.3)' : undefined,
              borderLeft: pos.left!==undefined ? '2px solid rgba(236,72,153,0.3)' : undefined,
              borderRight: pos.right!==undefined ? '2px solid rgba(236,72,153,0.3)' : undefined,
            }} />
          ))}

          <div style={{ textAlign:'center', marginBottom:24 }}>
            <div style={{ fontSize:9, fontWeight:800, letterSpacing:'0.2em', color:'rgba(249,168,212,0.5)', textTransform:'uppercase', marginBottom:10 }}>
              Broken Hearts
            </div>
            <h1 style={{ fontFamily:"'Cinzel',serif", fontSize:26, fontWeight:900, margin:'0 0 6px',
              background:'linear-gradient(to bottom,#fff,#f9a8d4)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
              Sala d'attesa
            </h1>
            <p style={{ margin:0, fontSize:13, color:'rgba(255,255,255,0.35)' }}>
              Cercando avversari{dots}
            </p>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'16px 0' }}>
            <div style={{ flex:1, height:1, background:'linear-gradient(to right,transparent,rgba(236,72,153,0.25))' }} />
            <span style={{ color:'rgba(236,72,153,0.4)', fontSize:14 }}>❤</span>
            <div style={{ flex:1, height:1, background:'linear-gradient(to left,transparent,rgba(236,72,153,0.25))' }} />
          </div>

          <div style={{ marginBottom:24 }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.15em', color:'rgba(255,255,255,0.3)', textTransform:'uppercase', textAlign:'center', marginBottom:14 }}>
              Giocatori in attesa
            </div>
            <div style={{ display:'flex', justifyContent:'center', gap:10 }}>
              {[...Array(4)].map((_,i) => (
                <motion.div key={i}
                  animate={i < queueSize ? { opacity:[0.7,1,0.7] } : {}}
                  transition={{ duration:1.5, repeat:Infinity }}
                  style={{
                    width:44, height:44, borderRadius:10,
                    background: i < queueSize ? 'linear-gradient(145deg,#ec4899,#be185d)' : 'rgba(255,255,255,0.04)',
                    border: `1.5px solid ${i < queueSize ? '#ec4899' : 'rgba(255,255,255,0.08)'}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    boxShadow: i < queueSize ? '0 0 16px rgba(236,72,153,0.5)' : 'none',
                    fontSize:20,
                  }}>
                  {i < queueSize ? '❤' : '·'}
                </motion.div>
              ))}
            </div>
            <div style={{ textAlign:'center', marginTop:12 }}>
              <span style={{ fontFamily:"'Cinzel',serif", fontSize:32, fontWeight:900,
                background:'linear-gradient(to bottom,#fff,#ec4899)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
                {queueSize}
              </span>
              <span style={{ fontSize:18, color:'rgba(236,72,153,0.4)', fontWeight:700 }}> / 4</span>
            </div>
          </div>

          <div style={{ height:3, borderRadius:2, background:'rgba(255,255,255,0.05)', overflow:'hidden' }}>
            <motion.div animate={{ x:['-100%','200%'] }} transition={{ duration:1.8, repeat:Infinity, ease:'easeInOut' }}
              style={{ height:'100%', width:'40%', background:'linear-gradient(to right,transparent,#ec4899,transparent)', borderRadius:2 }} />
          </div>

          <div style={{ textAlign:'center', marginTop:16, fontSize:12, color:'rgba(255,255,255,0.25)' }}>
            Connesso come <span style={{ color:'#f9a8d4', fontWeight:700 }}>{user.username}</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BhLobby;
